import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import type { StripeSubscriptionInfo } from '@/types/stripe';
import { formatStripeDate } from '@/utils/stripeFormatters';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { CalendarIcon, ExclamationTriangleIcon, XCircleIcon } from '@heroicons/react/24/outline';

const SUBSCRIPTION_STATUS_CONFIG: Record<string, { labelKey: string; className: string; icon?: React.ComponentType<{ className?: string; width?: number; height?: number }> }> = {
  active: { labelKey: 'subscriptionCard.statusActive', className: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircleIcon },
  trialing: { labelKey: 'subscriptionCard.statusTrialing', className: 'bg-blue-100 text-blue-800 border-blue-200', icon: CalendarIcon },
  past_due: { labelKey: 'subscriptionCard.statusPastDue', className: 'bg-amber-100 text-amber-800 border-amber-200', icon: ExclamationTriangleIcon },
  canceled: { labelKey: 'subscriptionCard.statusCanceled', className: 'bg-red-100 text-red-800 border-red-200', icon: XCircleIcon },
  unpaid: { labelKey: 'subscriptionCard.statusUnpaid', className: 'bg-red-200 text-red-900 border-red-300', icon: XCircleIcon },
};

function getSubscriptionStatusConfig(status: string | undefined) {
  if (!status) return null;
  const key = status.toLowerCase();
  return SUBSCRIPTION_STATUS_CONFIG[key] ?? { labelKey: status, className: 'bg-gray-100 text-gray-800 border-gray-200' };
}

interface StripeSubscriptionCardProps {
  subscription: StripeSubscriptionInfo | null | undefined;
}

export function StripeSubscriptionCard({ subscription }: StripeSubscriptionCardProps) {
  const { t } = useTranslation('billing');

  if (!subscription) {
    return <div className="text-sm text-gray-600">{t('subscriptionCard.noData')}</div>;
  }

  const statusConfig = getSubscriptionStatusConfig(subscription.status);
  const Icon = statusConfig?.icon;

  return (
    <div className="space-y-2">
      <div>
        <label className="text-sm font-medium text-gray-500">{t('subscriptionCard.label')}</label>
        <div className="text-sm text-gray-900 mt-1 font-mono break-all">
          {subscription.id || t('clientBilling.notAvailable')}
        </div>
        {subscription.status && statusConfig && (
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="outline" className={statusConfig.className}>
              {Icon && <Icon className="w-3.5 h-3.5 mr-1" width={14} height={14} />}
              {t(statusConfig.labelKey, statusConfig.labelKey)}
            </Badge>
          </div>
        )}
      </div>

      <div className="text-sm text-gray-600">
        {t('subscriptionCard.period', { start: formatStripeDate(subscription.current_period_start), end: formatStripeDate(subscription.current_period_end) })}
      </div>

      {subscription.cancel_at_period_end !== undefined && (
        <div className="text-sm text-gray-600">
          {t('subscriptionCard.cancelAtPeriodEnd')} {subscription.cancel_at_period_end ? t('subscriptionCard.yes') : t('subscriptionCard.no')}
        </div>
      )}
    </div>
  );
}

